// Gestión del CONSENTIMIENTO DE COOKIES / tecnologías de seguimiento de la PWA.
//
// Es la fuente de verdad en el cliente de qué categorías de tratamiento ha
// aceptado el visitante. Controla el arranque de la analítica de terceros (GA4
// y Contentsquare), que por diseño NO debe cargarse sin consentimiento previo
// (ePrivacy/GDPR). El estado se persiste en localStorage y se expone un pub/sub
// mínimo para que la capa de analítica reaccione a los cambios (conceder/retirar)
// sin acoplarse a React.
//
// Categorías:
//   - necessary: SIEMPRE true (cookies estrictamente necesarias; no se piden).
//   - analytics: carga de GA4 + Contentsquare (métricas de producto).
//   - replay:    Session Replay NO anonimizado de Contentsquare (requiere opt-in
//                explícito y separado de analytics; implica analytics).
//   - marketing: tecnologías de marketing/retargeting (reservado; hoy sin SDK).
//
// El consentimiento del lado servidor (prueba auditable en `consent_preference`)
// lo registra la capa que consume este store; aquí solo vive la preferencia
// local que gobierna la carga de scripts.

/** Categorías de consentimiento gestionadas por el banner. */
export type ConsentCategory = 'analytics' | 'replay' | 'marketing';

/** Estado de consentimiento persistido. `necessary` es implícito (siempre true). */
export interface CookieConsent {
  analytics: boolean;
  replay: boolean;
  marketing: boolean;
  /** Versión de la política de cookies vigente al decidir (para re-preguntar). */
  version: string;
  /** ISO timestamp de la decisión. */
  decidedAt: string;
}

/**
 * Versión de la política de cookies. Si se incrementa (p. ej. al añadir un
 * nuevo proveedor), una decisión anterior deja de considerarse vigente y el
 * banner vuelve a aparecer.
 */
export const COOKIE_CONSENT_VERSION = '1';

const STORAGE_KEY = 'saands.cookieConsent.v1';

/** Preferencia "todo denegado" (lo que rige mientras no haya decisión). */
const DENIED: Omit<CookieConsent, 'version' | 'decidedAt'> = {
  analytics: false,
  replay: false,
  marketing: false,
};

type Listener = (consent: CookieConsent | null) => void;
const listeners = new Set<Listener>();

// Caché en memoria para no golpear localStorage en cada lectura.
let cached: CookieConsent | null | undefined;

function readStorage(): CookieConsent | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    // Una decisión de versión anterior se considera caducada (re-preguntar).
    if (parsed.version !== COOKIE_CONSENT_VERSION) return null;
    return {
      analytics: parsed.analytics === true,
      replay: parsed.replay === true,
      marketing: parsed.marketing === true,
      version: COOKIE_CONSENT_VERSION,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : new Date().toISOString(),
    };
  } catch {
    // localStorage corrupto o bloqueado: tratamos como "sin decisión".
    return null;
  }
}

/**
 * Devuelve la decisión vigente, o `null` si el visitante AÚN NO ha decidido
 * (en ese caso el banner debe mostrarse y nada de analítica debe cargar).
 */
export function getConsent(): CookieConsent | null {
  if (cached === undefined) cached = readStorage();
  return cached;
}

/** ¿Ya decidió el visitante? (para saber si mostrar el banner). */
export function hasDecided(): boolean {
  return getConsent() !== null;
}

/**
 * Resuelve el valor efectivo de una categoría cuando aún no hay decisión:
 * SIEMPRE denegado (consentimiento previo). `replay` implica `analytics`.
 */
export function isGranted(category: ConsentCategory): boolean {
  const c = getConsent();
  if (!c) return false;
  if (category === 'replay') return c.analytics && c.replay;
  return c[category];
}

function notify(consent: CookieConsent | null): void {
  for (const listener of listeners) {
    try {
      listener(consent);
    } catch {
      // Un listener que falla no debe romper a los demás.
    }
  }
}

/**
 * Persiste una decisión de consentimiento y notifica a los suscriptores. El
 * flag `replay` solo tiene efecto si `analytics` también está concedido.
 */
export function setConsent(input: Partial<Omit<CookieConsent, 'version' | 'decidedAt'>>): CookieConsent {
  const base = getConsent() ?? { ...DENIED, version: COOKIE_CONSENT_VERSION, decidedAt: '' };
  const analytics = input.analytics ?? base.analytics;
  const next: CookieConsent = {
    analytics,
    // replay sin analytics no tiene sentido (Contentsquare es parte de analytics).
    replay: analytics ? (input.replay ?? base.replay) : false,
    marketing: input.marketing ?? base.marketing,
    version: COOKIE_CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
  };
  cached = next;
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Sin persistencia (modo privado, cuota): la decisión rige en memoria.
    }
  }
  notify(next);
  return next;
}

/** Acepta TODAS las categorías (botón "Aceptar todo"). */
export function acceptAll(): CookieConsent {
  return setConsent({ analytics: true, replay: true, marketing: true });
}

/** Rechaza todo lo opcional (botón "Solo necesarias"). */
export function rejectAll(): CookieConsent {
  return setConsent({ analytics: false, replay: false, marketing: false });
}

/**
 * Suscribe un listener a los cambios de consentimiento. Devuelve una función
 * para desuscribir. No se invoca con el estado actual al suscribir (hazlo tú si
 * lo necesitas con `getConsent()`).
 */
export function onConsentChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Limpia la caché en memoria (para pruebas). */
export function _resetConsentCacheForTests(): void {
  cached = undefined;
}
